'use client';

import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { deleteComment } from '../../../actions/reporterActions';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { getUserColor } from '../../../lib/utils/getUserColor';
import { CommentData } from '../../../types/shared';
import { useRealtime } from '../contexts/RealtimeProvider';
import { AddCommentForm } from './AddCommentForm';

// Интерфейс для узла дерева (комментарий + его дети)
interface CommentNode extends CommentData {
  children: CommentNode[];
}

// Рекурсивный компонент
const Comment = memo(function Comment({
  comment,
  photoId,
  onDelete,
  currentUserId,
  isAdmin,
  canReply,
}: {
  comment: CommentNode; // ✅ Принимаем уже с детьми!
  photoId: string;
  onDelete: (commentId: string) => void;
  currentUserId?: string;
  isAdmin: boolean;
  canReply: boolean;
}) {
  const [isReplying, setIsReplying] = useState(false);

  const isOwner = currentUserId === comment.user.id;
  const userNameColorStyle = useMemo(() => getUserColor(comment.user.id), [comment.user.id]);

  // Мемоизируем дату, чтобы не пересчитывать
  const timeAgo = useMemo(
    () => formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true, locale: ru }),
    [comment.createdAt],
  );

  const handleDelete = useCallback(() => {
    toast((t) => (
      <div className="flex flex-col gap-2 p-1">
        <p className="font-semibold text-slate-900">Удалить комментарий?</p>
        <div className="flex gap-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              onDelete(comment.id);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm font-semibold hover:bg-red-600"
          >
            Удалить
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-slate-200 text-slate-800 rounded text-sm font-semibold hover:bg-slate-300"
          >
            Отмена
          </button>
        </div>
      </div>
    ));
  }, [comment.id, onDelete]);

  return (
    <article id={`comment-${comment.id}`} className="flex items-start gap-3 group">
      <Image
        src={comment.user.image || '/default-avatar.jpg'}
        alt={comment.user.name || 'Аноним'}
        width={32}
        height={32}
        className="rounded-full mt-1 flex-shrink-0 border border-slate-700"
      />
      <div className="flex-1 min-w-0">
        <div className="bg-slate-800/80 rounded-2xl rounded-tl-none p-3 hover:bg-slate-800 transition-colors">
          <p className="font-bold text-sm">
            <span style={userNameColorStyle}>{comment.user.name || 'Аноним'}</span>
          </p>
          <p className="text-slate-200 whitespace-pre-wrap mt-1 text-sm leading-relaxed break-words">
            {comment.text}
          </p>
        </div>

        <div className="text-xs text-slate-400 flex items-center gap-3 mt-1.5 ml-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <time dateTime={comment.createdAt} itemProp="dateCreated" className="cursor-help">
                {timeAgo}
              </time>
            </TooltipTrigger>
            <TooltipContent>{new Date(comment.createdAt).toLocaleString('ru-RU')}</TooltipContent>
          </Tooltip>

          {canReply && (
            <button
              onClick={() => setIsReplying(!isReplying)}
              className="font-semibold hover:text-white transition-colors"
              aria-expanded={isReplying}
            >
              {isReplying ? 'Отмена' : 'Ответить'}
            </button>
          )}

          {isAdmin && (
            <button
              onClick={handleDelete}
              className="flex items-center gap-1 font-semibold text-red-600/80 hover:text-red-600 dark:text-red-500/70 dark:hover:text-red-500"
            >
              <Trash2 size={14} aria-hidden="true" /> Удалить
            </button>
          )}
        </div>

        <AnimatePresence>
          {isReplying && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 8 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              className="overflow-hidden"
            >
              <AddCommentForm
                photoId={photoId}
                parentId={comment.id}
                onCommentAdded={() => setIsReplying(false)}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ✅ Рендерим детей без фильтрации, они уже подготовлены! */}
        {comment.children.length > 0 && (
          <div className="mt-3 space-y-3 pl-3 border-l-2 border-slate-800">
            {comment.children.map((child) => (
              <Comment
                key={child.id}
                comment={child}
                photoId={photoId}
                onDelete={onDelete}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                canReply={canReply}
              />
            ))}
          </div>
        )}
      </div>
    </article>
  );
});

export function CommentsSection({
  photoId,
  scrollToCommentId,
  onCommentAdded,
}: {
  photoId: string;
  scrollToCommentId?: string | null;
  onCommentAdded?: (comment: CommentData) => void;
}) {
  const { comments, deleteOptimisticComment } = useRealtime(); // Получаем плоский список
  const [newCommentId, setNewCommentId] = useState<string | null>(null);
  const { data: session } = useSession();
  const currentUserId = session?.user?.id;
  const isAdmin = session?.user?.role === 'ADMIN';
  const canReply = Boolean(session?.user);

  // ✅ 1. Превращаем плоский список в дерево за O(N)
  // Это выполняется только когда меняется список комментариев
  const commentTree = useMemo(() => {
    if (!comments) return [];

    const map = new Map<string, CommentNode>();
    const roots: CommentNode[] = [];

    // Шаг 1: Создаем узлы и кладем в Map
    // Сортируем по дате сразу, чтобы порядок был верным
    const sortedComments = [...comments].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

    sortedComments.forEach((c) => {
      map.set(c.id, { ...c, children: [] });
    });

    // Шаг 2: Связываем детей с родителями
    sortedComments.forEach((c) => {
      const node = map.get(c.id)!;
      if (c.parentId && map.has(c.parentId)) {
        map.get(c.parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  }, [comments]);

  // Скролл к комментарию
  useEffect(() => {
    const targetId = newCommentId || scrollToCommentId;
    if (!targetId || !comments) return;

    // Небольшой таймаут, чтобы DOM успел построиться
    const timer = setTimeout(() => {
      const el = document.getElementById(`comment-${targetId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-comment'); // Добавь анимацию в CSS
        setTimeout(() => el.classList.remove('highlight-comment'), 2000);
        setNewCommentId(null);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [newCommentId, scrollToCommentId, comments]);

  const handleCommentAdded = useCallback(
    (newComment: CommentData) => {
      setNewCommentId(newComment.id);
      onCommentAdded?.(newComment);
    },
    [onCommentAdded],
  );

  const handleDelete = useCallback(
    (commentId: string) => {
      deleteOptimisticComment(commentId);
      toast.promise(deleteComment(commentId), {
        loading: 'Удаление...',
        success: 'Удалено',
        error: 'Ошибка удаления',
      });
    },
    [deleteOptimisticComment],
  );

  if (!comments) return <div className="text-center p-4 text-slate-500">Загрузка...</div>;

  return (
    <section className="space-y-4 pb-10">
      <h3 className="font-bold text-lg text-white flex items-center gap-2">
        Комментарии{' '}
        <span className="text-sm font-normal text-slate-400 bg-slate-800 px-2 py-0.5 rounded-full">
          {comments.length}
        </span>
      </h3>

      {session?.user ? (
        <div className="my-6">
          <AddCommentForm photoId={photoId} onCommentAdded={handleCommentAdded} />
        </div>
      ) : (
        <div className="text-center my-6 p-6 bg-slate-800/30 border border-slate-800 rounded-xl text-slate-400 text-sm">
          <Link
            href="/login"
            className="text-sky-400 hover:text-sky-300 font-semibold underline decoration-sky-400/30 underline-offset-4"
          >
            Войдите
          </Link>
          , чтобы присоединиться к обсуждению.
        </div>
      )}

      <div className="space-y-6">
        {commentTree.length > 0 ? (
          commentTree.map((rootNode) => (
            <Comment
              key={rootNode.id}
              comment={rootNode}
              photoId={photoId}
              onDelete={handleDelete}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              canReply={canReply}
            />
          ))
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-500">Пока нет комментариев.</p>
            <p className="text-slate-600 text-sm">Будьте первым, кто поделится мнением!</p>
          </div>
        )}
      </div>
    </section>
  );
}
